import React, { useContext } from 'react';
import T from 'prop-types';
import { isEmpty } from 'lodash-es';
import qs from 'qs';
import cx from 'classnames';

import Tooltip from '../../../../components/Tooltip';
import CsvExportButton from '../../../../components/Buttons/CsvExportButton';
import QuickViewContext from '../../../../components/QuickView/QuickViewContext';

import { reportTypesOptions } from '../../configs/research-library-config';
import { format } from '../../../../utils/date';
import { truncateString } from '../../../../utils/formatter';

const renderCompanies = (report, permissions, onCompanyQuickViewOpen) => {
  if (!report.research_report_companies || isEmpty(report.research_report_companies)) return null;

  const visibleCompanies = report.research_report_companies.slice(0, 5);
  const hiddenCompanies = report.research_report_companies.slice(5);

  const tooltipContent = () => (
    <div className="d-flex flex-column ignore-onclickoutside">
      {hiddenCompanies.map(({ ref_no, ticker }) => (
        <a
          key={ref_no}
          className="d-block m-b-xxs text-capitalize cursor-pointer"
          {...(permissions.allowCompanyKeyMetrics ? {
            onClick: () => onCompanyQuickViewOpen({ ref_no }),
          } : {
            href: `companies/${ref_no}`,
          })}

        >
          {ticker}
        </a>
      ))}
    </div>
  );

  return (
    <div className="report-companies d-flex justify-content-start ignore-onclickoutside">
      {visibleCompanies.map(({ ref_no, ticker }) => (
        <div key={ref_no} className="report-company text-uppercase">
          <a
            key={ref_no}
            className="text-uppercase cursor-pointer"
            {...(permissions.allowCompanyKeyMetrics ? {
              onClick: () => onCompanyQuickViewOpen({ ref_no }),
            } : {
              href: `companies/${ref_no}`,
            })}

          >
            {ticker && truncateString(ticker, 6)}
          </a>
        </div>
      ))}
      {!isEmpty(hiddenCompanies) && (
        <div className="text-uppercase report-companies-hidden">
          <Tooltip content={tooltipContent()}>
            <div className="report-hidden-company">{`+${hiddenCompanies.length}`}</div>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export const filterByType = (report, type) => {
  if (type !== 'all' && type !== 'most_downloaded') {
    const [one] = reportTypesOptions({}, true).filter(r => (r.value === type) && r);
    return [one.color, one.text];
  }
  const [first] = reportTypesOptions({}, true).filter(r => (r.value === report.report_types[0]) && r);
  return [first.color, first.text];
};

const Report = ({
  report,
  type,
  eventId,
  disabledLibrary,
  permissions,
}) => {
  const { onCompanyQuickViewOpen } = useContext(QuickViewContext);

  const downloadLink = () => {
    const params = { research_library: { event_id: eventId, id: report.id } };
    if (disabledLibrary) return null;
    return `/research_library/download?${qs.stringify(params)}`;
  };
  const mainTextTooltip = (
    <div className="report-text-tootip">
      <h2 className="report-text-tootip-heading">{report.title}</h2>
      <p className="report-text-tootip-description">{report.summary}</p>
    </div>
  );

  return (
    <div className={cx('report-card', { disabled: disabledLibrary })}>
      <div className="report-image-box">
        <img src={report.thumbnail.url} className="report-image" alt="report thumbnail" />
      </div>
      <div className="report-text">
        <div className="report-type">
          <h2>
            {filterByType(report, type)[1]}
          </h2>
          {report.new && <span className="badge">New</span>}
        </div>
        <Tooltip wrapChildrenInHtmlNode content={mainTextTooltip} disabled={disabledLibrary}>
          <h2 className="report-heading">{report.title}</h2>
        </Tooltip>
        {renderCompanies(report, permissions, onCompanyQuickViewOpen)}
        <div className="report-card-footer">
          <span className="date">{format((report.upload_date || report.created_at), 'MMMM DD, YYYY')}</span>
          <CsvExportButton
            url={downloadLink()}
            tooltipText="Download"
          />
        </div>
      </div>
    </div>
  );
};

Report.propTypes = {
  report: T.shape({
    id: T.number.isRequired,
    new: T.bool,
    title: T.string.isRequired,
    summary: T.string.isRequired,
    report_types: T.array,
    research_report_companies: T.array,
    created_at: T.string.isRequired,
    upload_date: T.string,
    thumbnail: T.shape({
      url: T.string.isRequired,
    }).isRequired,
  }).isRequired,
  type: T.string,
  eventId: T.number,
  disabledLibrary: T.bool,
  permissions: T.object,
};

export default Report;
